import { useCallback, useEffect, useState } from 'react';
import { VIEW_360_STEP } from './constants';
export function use360View({ images }) {
    const [clientX, setClientX] = useState(0);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isMouseDown, setIsMouseDown] = useState(false);
    const [isFirstMove, setIsFirstMove] = useState(false);
    const [isImagesLoading, setIsImagesLoading] = useState(false);
    const [isGuideShowing, setIsGuideShowing] = useState(true);
    const imagesLength = images.length;
    const maxIndex = images && imagesLength - 1;
    const updateState = (curIndex, position, firstMove = false) => {
        setClientX(position);
        setCurrentImageIndex(curIndex);
        if (firstMove) {
            setIsFirstMove(false);
            setIsGuideShowing(false);
        }
    };
    const onTouchStart = () => {
        setIsFirstMove(true);
    };
    const moveNext = (currentMousePositionX) => {
        if (isFirstMove) {
            const curIndex = currentImageIndex + 1;
            updateState(curIndex, currentMousePositionX, isFirstMove);
        }
        else {
            const itemsSkippedRight = Math.floor((currentMousePositionX - clientX) / VIEW_360_STEP) || 1;
            const curIndex = (currentImageIndex + itemsSkippedRight) % maxIndex || maxIndex;
            updateState(curIndex, currentMousePositionX);
        }
    };
    const movePrev = (currentMousePositionX) => {
        if (isFirstMove) {
            const curIndex = currentImageIndex - 1 > 0 ? currentImageIndex - 1 : maxIndex;
            updateState(curIndex, currentMousePositionX, isFirstMove);
        }
        else {
            const itemsSkippedLeft = Math.floor((clientX - currentMousePositionX) / VIEW_360_STEP) || 1;
            let curIndex;
            if (currentImageIndex - itemsSkippedLeft < 0) {
                curIndex = Math.abs(maxIndex + (currentImageIndex - itemsSkippedLeft) + 1);
            }
            else {
                curIndex = currentImageIndex - itemsSkippedLeft;
            }
            updateState(curIndex, currentMousePositionX);
        }
    };
    const chooseMoveDirection = (currentMousePositionX) => {
        let localClientX;
        if (clientX === 0 && isFirstMove) {
            setClientX(currentMousePositionX);
            localClientX = currentMousePositionX;
        }
        const coordinate = localClientX || clientX;
        if (currentMousePositionX - coordinate >= VIEW_360_STEP) {
            moveNext(currentMousePositionX);
        }
        if (coordinate - VIEW_360_STEP > currentMousePositionX) {
            movePrev(currentMousePositionX);
        }
    };
    const onMove = useCallback((event) => {
        if (isMouseDown && maxIndex > 0) {
            const currentMousePositionX = event.clientX;
            chooseMoveDirection(currentMousePositionX);
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientX, currentImageIndex, isFirstMove, isMouseDown, maxIndex]);
    const onMouseDown = (event) => {
        if ('button' in event && event.button === 0) {
            setIsFirstMove(true);
            setIsMouseDown(true);
        }
    };
    const onMouseUp = useCallback(() => {
        setIsMouseDown(false);
    }, []);
    const onTouchMove = useCallback((event) => {
        if (maxIndex > 0) {
            const currentMousePositionX = event?.touches[0].clientX;
            chooseMoveDirection(currentMousePositionX);
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientX, currentImageIndex, isFirstMove, maxIndex]);
    useEffect(() => {
        if (isMouseDown) {
            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, [isMouseDown, onMouseUp, onMove]);
    return {
        onTouchMove,
        onMouseUp,
        onTouchStart,
        onMouseDown,
        currentImageIndex,
        isImagesLoading,
        setIsImagesLoading,
        isGuideShowing,
    };
}
